
import {throttle} from "../lib/underscore.js";
import * as d3 from "../lib/d3.js";
import {makeLayerRenderer, makeDynamicRenderer} from "../canvas/twod.js";
import {report} from "../report.js";
import {$} from "../util/seq.js";


function buildRenderer(agents, inputController, orientation) {

    const {rendererAgent, viewboxAgent, globeAgent, mapAgent} = agents;
    const globe = globeAgent.value();
    const maps = mapAgent.value();
    if (!globe || !maps) {
        return;
    }

    report.status("Rendering Globe...");
    console.time("rendering map");

    const mapCtx = d3.select("#map").node().getContext("2d");
    const foregroundCtx = d3.select("#foreground").node().getContext("2d");
    const annotationSvg = d3.select("#annotation");

    // UNDONE: have the globe do this itself
    globe.setOrientation(orientation.get(), viewboxAgent.value());  // This allows the path function to work.

    const path = d3.geoPath().projection(globe.projection).pointRadius(d => d.size ?? 7);

    // Throttled draw method helps with slow devices that would get overwhelmed by too many redraw events.
    const REDRAW_WAIT = 5;  // milliseconds
    let doDraw_throttled = throttle(doDraw, REDRAW_WAIT, {leading: false});

    const background = globe.backgroundRenderer();
    const graticule = globe.graticuleRenderer();
    const preForeground = globe.preForegroundRenderer();
    const postForeground = globe.postForegroundRenderer();

    const {mapLo, mapHi} = maps;

    const dynamicItemsLo = makeDynamicRenderer();
    const dynamicItemsHi = makeDynamicRenderer();

    const mapRendererLo = makeLayerRenderer([background]);
    const mapRendererHi = makeLayerRenderer([background]);
    const fgRendererLo = makeLayerRenderer([preForeground, graticule, mapLo, dynamicItemsLo, postForeground]);
    const fgRendererHi = makeLayerRenderer([preForeground, graticule, mapHi, dynamicItemsHi, postForeground]);

    function canvasDraw(isHD) {
        const mapRenderer = isHD ? mapRendererHi : mapRendererLo;
        const foregroundRenderer = isHD ? fgRendererHi : fgRendererLo;
        mapRenderer.renderTo(mapCtx, path);
        foregroundRenderer.renderTo(foregroundCtx, path);
        path.context(null);
    }

    function doDraw() {
        annotationSvg.selectAll("path").attr("d", path);
        canvasDraw(false);
        rendererAgent.call("redraw");
        doDraw_throttled = throttle(doDraw, REDRAW_WAIT, {leading: false});
    }

    // Attach to map rendering events on input controller.

    inputController.on("moveStart.renderer", () => {
        rendererAgent.call("start");
    });
    inputController.on("move.renderer", () => {
        doDraw_throttled();
    });
    inputController.on("moveEnd.renderer", () => {
        annotationSvg.selectAll("path").attr("d", path);
        canvasDraw(true);
        rendererAgent.call("render");
    });

    // Finally, inject the globe model into the input controller. Do it on the next event turn to ensure
    // renderer is fully set up before events start flowing.
    Promise.resolve().then(() => {
        inputController.assignGlobe(globe);
    }).catch(report.error);

    console.timeEnd("rendering map");

    return {
        dynamicItemsLo,
        dynamicItemsHi,
        draw() {
            annotationSvg.selectAll("path").attr("d", path);
            canvasDraw(true);
        },
    };
}

export function defineRendererBuilder(agents, inputController, orientation) {
    const {rendererAgent, globeAgent, mapAgent} = agents;

    function startRendering() {
        rendererAgent.submit(() => buildRenderer(agents, inputController, orientation));
    }

    globeAgent.on($`update`, startRendering);
    mapAgent.on($`update`, startRendering);
}
